import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Brain, Building, Film, Heart, ArrowRight, Briefcase, Smartphone, Package, Utensils, Sparkles, ExternalLink } from 'lucide-react';
import useInView from '../hooks/useInView';

const SolutionsSection = () => {
  const [activeTab, setActiveTab] = useState('services');
  const [headerRef, headerInView] = useInView();
  const [tabsRef, tabsInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  const services = [
    {
      logo: null,
      icon: <CheckCircle2 size={32} />,
      title: 'ICA – Business Compliance Management',
      shortTitle: 'ICA',
      description: 'VDM is the Channel partner for ICA. Simplifies and automates business compliance processes, saving time and reducing errors while ensuring regulatory adherence.',
      features: ['Automated Compliance Tracking', 'Real-time Alerts & Notifications', 'Regulatory Updates', 'Audit Trail Management'],
      image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095',
      link: '/ica'
    },
    {
      logo: 'https://customer-assets.emergentagent.com/job_biz-innovations/artifacts/m9542jkn_Zenthara%20Final%20Logo.jpeg',
      icon: <Building size={32} />,
      title: 'Zenthara – Commercial Space Leasing',
      shortTitle: 'Zenthara',
      description: 'Elevating commercial horizons through intelligent leasing, innovative brand placements, and space-on-hire solutions.',
      features: ['Space Leasing & Brand Placement', 'Space on Hire (SOH)', 'Pop-up Store Setups', 'Flea Market Organization'],
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab',
      link: '/zenthara'
    },
    {
      logo: 'https://customer-assets.emergentagent.com/job_vdmbusiness/artifacts/v6hrcwjd_ChatGPT%20Image%20Jan%201%2C%202026%2C%2009_07_53%20PM.png',
      icon: <Utensils size={32} />,
      title: 'CafetriX Labs – Cafeteria Operations Intelligence',
      shortTitle: 'CafetriX Labs',
      description: 'Transform corporate cafeterias into strategic assets with 360° operational audits and implementation support.',
      features: ['360° Operational Audit', 'Menu & Kitchen Optimization', 'Win-Win Commercial Structuring', 'Food Waste Management'],
      image: 'https://images.unsplash.com/photo-1567521464027-f127ff144326',
      link: '/cafetrix-labs'
    },
    {
      logo: 'https://customer-assets.emergentagent.com/job_fd78a43a-475d-4d4e-ba79-642a920f6f2b/artifacts/0qvy7y6q_generated-image%20%2843%29.png',
      icon: <Sparkles size={32} />,
      title: 'KALYV – Wellness Innovations',
      shortTitle: 'KALYV',
      description: 'A new-age wellness brand redesigning the experience of being well — at the intersection of science and soul. Live Completely. Live Purposefully.',
      features: ['Holistic Physical & Mental Health', 'Innovation with Empathy', 'Individual + Community Well-being', 'Rooted in Indian Wisdom, Future-Facing'],
      image: 'https://images.unsplash.com/photo-1758274538961-fe8f1f24166f',
      link: '/kalyv'
    }
  ];

  const applications = [
    {
      logo: 'https://customer-assets.emergentagent.com/job_biz-innovations/artifacts/p7vyj2z6_Pivot%20mint%20blck%20Logo.jpeg',
      icon: <Brain size={32} />,
      title: 'PivotMint – AI Career Discovery',
      shortTitle: 'PivotMint',
      description: 'Helping students and professionals find their ideal careers using science-backed assessments and AI-driven insights.',
      features: ['AI-Powered Career Matching', 'Personality Assessments', 'Skill Gap Analysis', 'Career Path Recommendations'],
      image: 'https://images.unsplash.com/photo-1606857521015-7f9fcf423740',
      link: '/pivotmint'
    },
    {
      logo: 'https://customer-assets.emergentagent.com/job_biz-innovations/artifacts/6p8sodon_Maitreya%20Final%20Logo.jpeg',
      icon: <Heart size={32} />,
      title: 'Maitreya – Companion for Life',
      shortTitle: 'Maitreya',
      description: 'An empathetic AI emotional support and motivation assistant offering 24/7 conversations without judgment.',
      features: ['24/7 Empathetic Conversations', 'Adaptive Mood Recognition', 'Personalized Motivation', 'Mindfulness Guidance'],
      image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773',
      link: null
    },
    {
      logo: null,
      icon: <Smartphone size={32} />,
      title: 'tapback – QR-First AI Feedback Platform',
      shortTitle: 'tapback',
      description: 'Collects star ratings, instantly serves AI-generated reason tags, detects fraud, and escalates critical complaints to the right person — all before your customer walks out the door.',
      features: ['Multi-Agent GPT-5.2 AI', 'Fraud Detection & Genuine Score', 'Instant Email & WhatsApp Escalations', 'Auto-Reply Drafts'],
      image: 'https://images.unsplash.com/photo-1770096841724-68b77afefdde',
      link: '/tapback'
    }
  ];

  const products = [
    {
      logo: 'https://customer-assets.emergentagent.com/job_biz-innovations/artifacts/ny87boz7_generated-image%20%2811%29.png',
      icon: <Building size={32} />,
      title: 'Nuvora – Food Innovations',
      shortTitle: 'Nuvora',
      description: 'Comprehensive F&B solutions including food waste management, wastewater bio-solutions, and antimicrobial protection.',
      features: ['Digestor & Dewaterer Systems', 'Bio-enzyme Wastewater Solutions', 'KleenClad Antimicrobial Cladding', 'Dycem Contamination Control'],
      image: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d',
      link: '/nuvora'
    }
  ];

  const tabs = [
    { id: 'services', label: 'Services', icon: <Briefcase size={20} />, data: services, description: 'Professional business solutions and consulting services' },
    { id: 'applications', label: 'Applications', icon: <Smartphone size={20} />, data: applications, description: 'AI-powered applications and digital platforms' },
    { id: 'products', label: 'Products', icon: <Package size={20} />, data: products, description: 'Innovative physical products and solutions' }
  ];

  const currentTab = tabs.find(tab => tab.id === activeTab);

  const renderSolutionCard = (solution, index) => (
    <div 
      key={index} 
      className="stagger-in"
      style={{
        '--i': index,
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid var(--border-subtle)',
        overflow: 'hidden',
        transition: 'border-color 0.3s ease-in-out'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--brand-primary)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-subtle)';
      }}
    >
      {/* Image */}
      <div style={{ height: '200px', overflow: 'hidden' }}>
        <img 
          src={solution.image} 
          alt={solution.title} 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.4s ease-in-out'
          }}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        />
      </div>

      {/* Content */}
      <div style={{ padding: '30px' }}>
        {/* Logo or Icon */}
        <div style={{ marginBottom: '16px', height: '60px', display: 'flex', alignItems: 'center' }}>
          {solution.logo ? (
            <img 
              src={solution.logo} 
              alt={solution.title}
              style={{
                height: '60px',
                objectFit: 'contain'
              }}
            />
          ) : solution.title.includes('ICA') ? (
            <div>
              <h2 style={{
                fontSize: '32px',
                fontWeight: '700',
                color: 'var(--brand-primary)',
                letterSpacing: '2px'
              }}>ICA</h2>
            </div>
          ) : solution.shortTitle === 'KALYV' ? (
            <div>
              <h2 style={{
                fontSize: '32px',
                fontWeight: '700',
                color: '#c49aff',
                letterSpacing: '4px'
              }}>KAL<span style={{ color: '#e0c8ff' }}>YV</span></h2>
            </div>
          ) : solution.shortTitle === 'tapback' ? (
            <div>
              <h2 style={{
                fontSize: '32px',
                fontWeight: '800',
                color: 'var(--brand-primary)',
                letterSpacing: '-1px',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>tap<span style={{ color: '#ffffff' }}>back</span></h2>
            </div>
          ) : (
            <div style={{ color: 'var(--brand-primary)' }}>
              {solution.icon}
            </div>
          )}
        </div>

        <h3 className="heading-2" style={{ marginBottom: '12px' }}>
          {solution.title}
        </h3>
        <p className="body-small" style={{ marginBottom: '20px', color: 'var(--text-muted)', lineHeight: '1.6' }}>
          {solution.description}
        </p>

        {/* Features */}
        <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px' }}>
          {solution.features.slice(0, 3).map((feature, idx) => (
            <li key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
              fontSize: '13px',
              color: 'var(--text-muted)'
            }}>
              <CheckCircle2 size={14} color="var(--brand-primary)" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {/* Button */}
        {solution.link ? (
          solution.external ? (
            <a 
              href={solution.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-secondary" 
              data-testid={`learn-more-${solution.shortTitle.toLowerCase().replace(/\s+/g, '-')}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none',
                fontSize: '14px',
                padding: '10px 20px'
              }}
            >
              More Information
              <ExternalLink size={16} />
            </a>
          ) : (
            <Link to={solution.link} className="btn-secondary" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              textDecoration: 'none',
              fontSize: '14px',
              padding: '10px 20px'
            }}>
              Learn More
              <ArrowRight size={16} />
            </Link>
          )
        ) : (
          <button className="btn-secondary" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            padding: '10px 20px'
          }}>
            Learn More
            <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <section id="solutions" style={{
      background: 'var(--bg-primary)',
      padding: '100px 7.6923%',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`reveal-root ${headerInView ? 'is-in-view' : ''}`}
          style={{ textAlign: 'center', marginBottom: '50px' }}
        >
          <div className="reveal" style={{
            '--i': 0,
            display: 'inline-block',
            padding: '8px 16px',
            background: 'rgba(0, 255, 209, 0.1)',
            border: '1px solid var(--brand-primary)',
            marginBottom: '20px',
            fontSize: '14px',
            color: 'var(--brand-primary)',
            fontWeight: '500'
          }}>
            OUR SOLUTIONS
          </div>
          <h2 className="display-large reveal" style={{ '--i': 1, marginBottom: '20px' }}>
            Intelligent <span style={{ color: 'var(--brand-primary)' }}>Innovations</span> for Your Business
          </h2>
          <p className="body-medium reveal" style={{ '--i': 2, maxWidth: '800px', margin: '0 auto' }}>
            Discover our suite of cutting-edge solutions designed to address complex business challenges with simplicity and intelligence.
          </p>
        </div>

        {/* Tabs */}
        <div
          ref={tabsRef}
          className={`reveal-root ${tabsInView ? 'is-in-view' : ''}`}
        >
          <div className="reveal solutions-tab-strip" style={{
            '--i': 0,
            display: 'flex',
            justifyContent: 'center',
            gap: '0',
            marginBottom: '50px',
            borderBottom: '2px solid var(--border-subtle)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="solutions-tab-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '16px 40px',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                background: activeTab === tab.id ? 'rgba(0, 255, 209, 0.1)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid var(--brand-primary)' : '3px solid transparent',
                color: activeTab === tab.id ? 'var(--brand-primary)' : 'var(--text-muted)',
                fontSize: '16px',
                fontWeight: activeTab === tab.id ? '600' : '400',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '-2px'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = 'var(--text-primary)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = 'var(--text-muted)';
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {tab.icon}
              {tab.label}
              <span style={{
                background: activeTab === tab.id ? 'var(--brand-primary)' : 'var(--border-subtle)',
                color: activeTab === tab.id ? 'var(--bg-solid)' : 'var(--text-muted)',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {tab.data.length}
              </span>
            </button>
          ))}
          </div>

          {/* Tab Description */}
          <div className="reveal" style={{ '--i': 1, textAlign: 'center', marginBottom: '40px' }}>
            <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
              {currentTab?.description}
            </p>
          </div>
        </div>

        {/* Solutions Grid */}
        <div
          key={activeTab}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '30px'
          }}
        >
          {currentTab?.data.map((solution, index) => renderSolutionCard(solution, index))}
        </div>

        {/* Future Innovations */}
        <div
          ref={ctaRef}
          className={`reveal-root ${ctaInView ? 'is-in-view' : ''}`}
        >
          <div className="reveal" style={{
            '--i': 0,
            marginTop: '80px',
            padding: '60px',
            background: 'rgba(0, 255, 209, 0.05)',
            border: '1px solid var(--brand-primary)',
            textAlign: 'center'
          }}>
            <h3 className="heading-1" style={{ marginBottom: '20px' }}>
              More Innovations Coming Soon
            </h3>
            <p className="body-medium" style={{ maxWidth: '700px', margin: '0 auto 30px' }}>
              We&apos;re constantly evolving with future business needs, developing new solutions that push the boundaries of what&apos;s possible.
            </p>
            <button className="btn-primary">
              Stay Updated
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .solution-item {
            grid-template-columns: 1fr !important;
          }
          .solution-item > div {
            order: 1 !important;
          }
          /* Compact tabs + horizontal-scroll strip on mobile */
          .solutions-tab-strip {
            justify-content: flex-start !important;
          }
          .solutions-tab-btn {
            padding: 12px 18px !important;
            font-size: 14px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default SolutionsSection;
